import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import NHLogo from '../components/NHLogo';

function Setup() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [, setPasswordRequirements] = useState([]);
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
  });

  const { completeSetup } = useAuth();
  const navigate = useNavigate();

  // Load password requirements on mount
  useEffect(() => {
    const loadRequirements = async () => {
      const result = await authService.getPasswordRequirements();
      if (result.requirements) {
        setPasswordRequirements(result.requirements);
      }
    };
    loadRequirements();
  }, []);

  // Validate password as user types
  useEffect(() => {
    const password = formData.password;
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
    });
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setErrors([]);
  };

  const isPasswordValid = () => {
    return Object.values(passwordValidation).every(v => v);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    // Client-side validation
    const validationErrors = [];

    if (!formData.email) {
      validationErrors.push('Email is required');
    }
    if (!formData.firstName) {
      validationErrors.push('First name is required');
    }
    if (!formData.lastName) {
      validationErrors.push('Last name is required');
    }
    if (!isPasswordValid()) {
      validationErrors.push('Password does not meet requirements');
    }
    if (formData.password !== formData.confirmPassword) {
      validationErrors.push('Passwords do not match');
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    const result = await completeSetup(formData);

    if (result.success) {
      navigate('/login', {
        state: { message: 'Setup complete! Please sign in with your new account.' }
      });
    } else {
      setErrors(result.errors || [result.error || 'Setup failed']);
    }

    setIsLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.setupCard}>
        <div style={styles.header}>
          <NHLogo size={36} style={{ justifyContent: 'center' }} />
          <p style={styles.subtitle}>Initial Setup</p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <h2 style={styles.formTitle}>Create Super Admin Account</h2>
          <p style={styles.description}>
            This is the first time the system is being set up. Create your Super Admin account to get started.
          </p>

          {errors.length > 0 && (
            <div style={styles.error}>
              <ul style={styles.errorList}>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter first name"
                required
                style={styles.input}
                disabled={isLoading}
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label} htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter last name"
                required
                style={styles.input}
                disabled={isLoading}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              style={styles.input}
              disabled={isLoading}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              required
              style={styles.input}
              disabled={isLoading}
            />
            <div style={styles.requirements}>
              <span style={styles.requirementLabel}>Password must have:</span>
              <ul style={styles.requirementList}>
                <li style={passwordValidation.minLength ? styles.valid : styles.invalid}>
                  At least 8 characters
                </li>
                <li style={passwordValidation.hasUppercase ? styles.valid : styles.invalid}>
                  At least one uppercase letter (A-Z)
                </li>
                <li style={passwordValidation.hasLowercase ? styles.valid : styles.invalid}>
                  At least one lowercase letter (a-z)
                </li>
                <li style={passwordValidation.hasNumber ? styles.valid : styles.invalid}>
                  At least one number (0-9)
                </li>
              </ul>
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label} htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required
              style={styles.input}
              disabled={isLoading}
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <span style={styles.mismatch}>Passwords do not match</span>
            )}
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(isLoading ? styles.buttonDisabled : {})
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Create Account & Start'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  setupCard: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
    width: '100%',
    maxWidth: '500px',
    overflow: 'hidden',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '30px',
    textAlign: 'center',
    color: '#fff',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: '700',
  },
  subtitle: {
    margin: '8px 0 0',
    fontSize: '14px',
    opacity: 0.9,
  },
  form: {
    padding: '30px',
  },
  formTitle: {
    margin: '0 0 12px',
    fontSize: '20px',
    color: '#333',
    textAlign: 'center',
  },
  description: {
    margin: '0 0 24px',
    fontSize: '14px',
    color: '#666',
    textAlign: 'center',
    lineHeight: '1.5',
  },
  error: {
    background: '#fee',
    color: '#c00',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  errorList: {
    margin: 0,
    padding: '0 0 0 20px',
  },
  row: {
    display: 'flex',
    gap: '16px',
  },
  inputGroup: {
    marginBottom: '20px',
    flex: 1,
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  requirements: {
    marginTop: '10px',
    padding: '12px',
    background: '#f8f9fa',
    borderRadius: '6px',
    fontSize: '13px',
  },
  requirementLabel: {
    fontWeight: '500',
    color: '#555',
    display: 'block',
    marginBottom: '8px',
  },
  requirementList: {
    margin: 0,
    padding: '0 0 0 20px',
    listStyle: 'none',
  },
  valid: {
    color: '#28a745',
    marginBottom: '4px',
  },
  invalid: {
    color: '#999',
    marginBottom: '4px',
  },
  mismatch: {
    color: '#dc3545',
    fontSize: '13px',
    marginTop: '4px',
    display: 'block',
  },
  button: {
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.2s',
    marginTop: '10px',
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
};

export default Setup;
